/* User details — profile, assigned roles and the effective (merged) permission
   matrix the user resolves to across all their roles. */

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import s from "./userManagement.module.css";
import {
  IoArrowBack,
  IoPencil,
  IoKeyOutline,
  IoTrashOutline,
  IoMailOutline,
  IoCallOutline,
  IoBriefcaseOutline,
  IoCalendarOutline,
  IoTimeOutline,
} from "react-icons/io5";
import { getUser, deleteUser, setUserStatus, resetPassword } from "../../rbac/rbacService";
import { useRbac } from "../../rbac/RbacContext";
import { mergePermissions, countGranted, TOTAL_PERMISSION_COUNT } from "../../rbac/permissions";
import { showToast } from "../../utils/Toastify/Toastify";
import { formatDate } from "../../utils/dateFormatter";
import { Avatar, StatusBadge, RoleChip, Spinner, Toggle, ConfirmDialog } from "./uiBits";
import PermissionMatrix from "./PermissionMatrix";
import UserFormModal from "./UserFormModal";

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { roles } = useRbac();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Is this an Admin (system-role) account? Its activate/deactivate is protected.
  const isProtectedAdmin = (user?.roleIds || []).some((rid) => {
    const role = roles.find((r) => r.id === rid);
    return role && role.system && role.name === "Admin";
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUser(id);
      setUser(data);
    } catch {
      showToast("User not found", "error");
      navigate("/mainlayout/user-management");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className={s.page}>
        <Spinner label="Loading user…" />
      </div>
    );
  }
  if (!user) return null;

  const userRoles = (user.roleIds || [])
    .map((rid) => roles.find((r) => r.id === rid))
    .filter(Boolean);
  const effective = mergePermissions(...userRoles.map((r) => r.permissions));
  const grantedCount = countGranted(effective);

  const toggleStatus = async () => {
    const next = user.status === "active" ? "inactive" : "active";
    setUser((u) => ({ ...u, status: next }));
    try {
      await setUserStatus(user.id, next);
      showToast(next === "active" ? "User activated" : "User deactivated", "success");
    } catch {
      showToast("Failed to update status", "error");
      load();
    }
  };

  const runConfirm = async () => {
    setConfirmLoading(true);
    try {
      if (confirm === "delete") {
        await deleteUser(user.id);
        showToast("User deleted", "success");
        navigate("/mainlayout/user-management");
      } else if (confirm === "reset") {
        await resetPassword(user.id);
        showToast("Password reset link sent", "success");
        setConfirm(null);
      }
    } catch (err) {
      showToast(err.message || "Action failed", "error");
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div className={s.page}>
      <button className={s.backLink} onClick={() => navigate("/mainlayout/user-management")}>
        <IoArrowBack /> Back to users
      </button>

      <div className={s.detailGrid}>
        {/* Profile card */}
        <div className={`${s.detailCard} ${s.profileCard}`}>
          <Avatar name={user.name} color={user.avatarColor} size={72} />
          <div className={s.profileName}>{user.name}</div>
          {user.title && <div className={s.profileTitle}>{user.title}</div>}
          <StatusBadge status={user.status} />

          <div className={s.profileDivider} />

          <div className={s.profileMeta}>
            <div className={s.metaRow}>
              <span className={s.metaLabel}>
                <IoMailOutline /> Email
              </span>
              <span className={s.metaValue}>{user.email}</span>
            </div>
            <div className={s.metaRow}>
              <span className={s.metaLabel}>
                <IoCallOutline /> Phone
              </span>
              <span className={s.metaValue}>{user.phone || "—"}</span>
            </div>
            <div className={s.metaRow}>
              <span className={s.metaLabel}>
                <IoBriefcaseOutline /> Title
              </span>
              <span className={s.metaValue}>{user.title || "—"}</span>
            </div>
            <div className={s.metaRow}>
              <span className={s.metaLabel}>
                <IoCalendarOutline /> Created
              </span>
              <span className={s.metaValue}>{formatDate(user.createdAt)}</span>
            </div>
            <div className={s.metaRow}>
              <span className={s.metaLabel}>
                <IoTimeOutline /> Last active
              </span>
              <span className={s.metaValue}>
                {user.lastActive ? formatDate(user.lastActive) : "Never"}
              </span>
            </div>
            <div className={s.metaRow}>
              <span className={s.metaLabel}>Account active</span>
              {/* Invited users haven't accepted yet — activating here would
                  strand the invite. Admins are protected. Toggle disabled. */}
              <Toggle
                checked={user.status === "active"}
                disabled={user.status === "invited" || isProtectedAdmin}
                onChange={toggleStatus}
              />
            </div>
          </div>

          <div className={s.profileActions}>
            <button className={s.btnPrimary} onClick={() => setEditOpen(true)}>
              <IoPencil /> Edit user
            </button>
            <button className={s.btnGhost} onClick={() => setConfirm("reset")}>
              <IoKeyOutline /> Reset password
            </button>
            <button className={s.btnDanger} onClick={() => setConfirm("delete")}>
              <IoTrashOutline /> Delete user
            </button>
          </div>
        </div>

        {/* Roles + permissions */}
        <div>
          <div className={s.detailCard} style={{ marginBottom: 18 }}>
            <div className={s.sectionTitle}>Assigned roles</div>
            <p className={s.sectionDesc}>
              This user inherits the combined permissions of every role below.
            </p>
            <div className={s.detailRoles}>
              {userRoles.length > 0 ? (
                userRoles.map((r) => <RoleChip key={r.id} role={r} />)
              ) : (
                <span className={s.userEmail}>No roles assigned</span>
              )}
            </div>
          </div>

          <div className={s.detailCard}>
            <div className={s.sectionTitle}>
              Effective permissions
              <span className={s.roleCardStat}>
                <strong>{grantedCount}</strong>/{TOTAL_PERMISSION_COUNT}
              </span>
            </div>
            <p className={s.sectionDesc}>
              The merged result of all assigned roles — what this user can
              actually do.
            </p>
            <PermissionMatrix value={effective} readOnly />
          </div>
        </div>
      </div>

      <UserFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        editingUser={user}
        roles={roles}
        onSaved={load}
      />

      <ConfirmDialog
        open={confirm === "delete"}
        danger
        title="Delete user?"
        message={`This will permanently remove ${user.name}. This action cannot be undone.`}
        confirmLabel="Delete user"
        loading={confirmLoading}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === "reset"}
        title="Reset password?"
        message={`Send a password reset to ${user.email}?`}
        confirmLabel="Send reset"
        loading={confirmLoading}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default UserDetails;
